import React from 'react';
import { Check, X, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';
import { API_CONFIG } from '@/config';

interface PersonalInfoRequest {
  id: string;
  requester_id: string;
  requester_name: string;
  phone_number: string;
  status: 'pending' | 'approved' | 'rejected';
  full_name: string;
  description: string;
  photo_url: string | null;
  resume_url: string | null;
  is_resume_public: number;
  created_at: string;
  updated_at: string;
  forSelf: number;
  type: 'info';
}

interface PersonalInfoRequestListProps {
  requests: PersonalInfoRequest[];
  isAdmin: boolean;
  onReview: (request: PersonalInfoRequest) => void;
  onReject: (requestId: string) => void;
}

export function PersonalInfoRequestList({ 
  requests, 
  isAdmin, 
  onReview, 
  onReject 
}: PersonalInfoRequestListProps) {
  if (requests.length === 0) {
    return <p className="p-4 text-center text-gray-500">لا توجد طلبات معلقة</p>;
  }

  return (
    <div className="bg-white shadow-sm rounded-lg divide-y">
      {requests.map((request) => (
        <div key={request.id} className="p-4">
          <div className="flex justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {/* <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                  إضافة معلومات
                </span> */}
                <span className={`text-xs px-2 py-1 rounded-lg ${
                  request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  request.status === 'approved' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {request.status === 'pending' ? 'معلق' : 
                   request.status === 'approved' ? 'مقبول' : 'مرفوض'}
                </span>
                <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                  {request.forSelf === 1 ? 'معلومات شخصية' : 'معلومات شخص آخر'}
                </span>
              </div>

              <div className="text-sm text-gray-600 space-y-1">
                <p>
                  <span className="font-medium ml-1">الاسم:</span>
                  {request.full_name}
                </p>
                <p>
                  <span className="font-medium ml-1">مقدم الطلب:</span>
                  {request.requester_name}
                </p>
                <p className="text-gray-500">{request.description}</p>
              </div>

              <div className="flex gap-4 mt-4">
                {request.photo_url && (
                  <div className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-gray-400" />
                    <a 
                      href={request.photo_url ? API_CONFIG.BASE_URL + "/" + request.photo_url : ""} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      عرض الصورة
                    </a>
                  </div>
                )}
                {request.resume_url && (
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <a 
                      href={request.resume_url ? API_CONFIG.BASE_URL + "/" + request.resume_url : ""} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      عرض السيرة الذاتية
                    </a>
                  </div>
                )}
              </div>

              <div className="text-xs text-gray-500">
                {format(parseISO(request.created_at), 'PPpp', { locale: ar })}
              </div>
            </div>

            {request.status === 'pending' && isAdmin && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onReview(request)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  title="مراجعة الطلب"
                >
                  <Check className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onReject(request.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="رفض الطلب"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}